import { z } from "zod"
import { NativeCapabilityError, nativeBridgeVersion, requestNativeCapability, supportsNativeCapability } from "./native-capabilities.ts"
import { safeNextRoute } from "./safe-route.ts"

const lifecycleSchema = z.enum(["resumed", "inactive", "hidden", "paused", "detached"])
const inset = z.number().finite().nonnegative()
const safeAreaSchema = z.strictObject({ top: inset, right: inset, bottom: inset, left: inset })
const stateSchema = z.strictObject({ appVersion: z.string().min(1), platform: z.enum(["ANDROID", "IOS"]), lifecycleState: lifecycleSchema, safeArea: safeAreaSchema })
const eventSchema = z.discriminatedUnion("event", [
  z.strictObject({ version: z.literal(nativeBridgeVersion), nonce: z.string().min(1), event: z.literal("lifecycleChanged"), data: z.strictObject({ state: lifecycleSchema }) }),
  z.strictObject({ version: z.literal(nativeBridgeVersion), nonce: z.string().min(1), event: z.literal("safeAreaChanged"), data: z.strictObject({ safeArea: safeAreaSchema }) }),
])

export type NativeAppState = z.infer<typeof stateSchema>
export type NativeAppEvent = z.infer<typeof eventSchema>

const command = async (method: "navigate" | "back" | "refresh" | "close" | "openExternal", params: Readonly<Record<string, string>> = {}): Promise<void> => {
  await requestNativeCapability(method, params)
}

/** UI, history policy and data refresh decisions stay in Web. */
export const nativeApp = {
  supports: supportsNativeCapability,
  async getState(): Promise<NativeAppState> {
    const result = stateSchema.safeParse(await requestNativeCapability("getAppState"))
    if (!result.success) throw new NativeCapabilityError("INVALID_RESPONSE")
    return result.data
  },
  async navigate(path: string, mode: "auto" | "replace" = "auto"): Promise<void> {
    if (path.length > 4096 || safeNextRoute(path) === null || !["auto", "replace"].includes(mode)) throw new NativeCapabilityError("INVALID_INPUT")
    return command("navigate", { path, mode })
  },
  async back(): Promise<void> {
    if (typeof window !== "undefined" && window.__cashhubHandleNativeBack?.() === true) return
    await command("back")
  },
  setBackSwipe: (enabled: boolean): Promise<Readonly<Record<string, unknown>>> => requestNativeCapability("setBackSwipe", { enabled: String(enabled) }),
  refresh: (): Promise<void> => command("refresh"),
  close: (): Promise<void> => command("close"),
  async openExternal(url: string): Promise<void> {
    if (url.length > 4096 || !z.url().safeParse(url).success) throw new NativeCapabilityError("INVALID_INPUT")
    return command("openExternal", { url })
  },
  subscribe(listener: (event: NativeAppEvent) => void): () => void {
    if (typeof window === "undefined" || window !== window.top) return () => undefined
    const receive = (event: Event) => {
      const parsed = eventSchema.safeParse((event as CustomEvent<unknown>).detail)
      const session = window.__cashhubNativeSession
      if (!parsed.success || session?.version !== nativeBridgeVersion || parsed.data.nonce !== session.nonce || !session.events?.includes(parsed.data.event)) return
      listener(parsed.data)
    }
    window.addEventListener("cashhub:native-app-event", receive)
    return () => window.removeEventListener("cashhub:native-app-event", receive)
  },
}
